import { useToast } from '@bv/composables/common/toast';
import { getRouter } from '@bv/plugins/router';
import { useUserStore } from '@bv/stores';
import { trans } from '@bv/utils/i18n';
import moment from 'moment';
import { defineStore } from 'pinia';
import { unref } from 'vue';

import conversationsApi from '@/api/conversations/conversations.api';
import WorkflowApi from '@/api/workflow/workflow.api';
import conditionsDateType from '@/constants/conversations/channelsRoles/channels-close-date-range.constants';
import { toPlain } from '@/helpers/conversations/to-plain.helper';
import { conversationWorker } from '@/helpers/conversations/worker-service.helper';
import socket from '@/helpers/socket/socket';
import conversationsSocket from '@/helpers/socket/socket';
import {
  type ConversationType,
  type MessageType,
  type MessagesDataType,
  type ConversationsUserType,
  type ConversationsTeamType,
  WorkflowStatusEnum,
  ConversationsRequestParamsType,
  RequestUrlDestinationConst,
  ConversationsFetchOptions,
  ConversationsSortingConst,
  CustomFilterAssignTypeConst,
  ConversationsStatusConst,
  AssignmentValueType,
  ReplyTypeConst,
} from '@/types/conversation.types';
import { DeleteContactType } from '@/types/events/delete-contact';
import type { MessagesStatusUpdate } from '@/types/events/messages-status-update';
import { TypingType } from '@/types/events/typing';
import { DefaultMetaType } from '@/types/general';
import {
  AllStateType,
  RequestQuery,
  ConversationsFiltrationParams,
  CachedMessagesType,
  AsidePanelStateEnum,
  UiStateEnum,
  ConversationUsersTyping,
} from '@/types/stores/conversationsSocket';
import type { tagType } from '@/types/tag';

// ===================================== constants =====================================
const storedAsidePanelState = localStorage.getItem('asidePanelState');
const initialAsidePanelState = storedAsidePanelState ? JSON.parse(storedAsidePanelState) : { contact: false }; // default value

export const useConversationsStore = defineStore('conversations', {
  state: (): AllStateType => ({
    // socket connection data
    socketConnection: false,
    connectionError: null,
    reconnectionPopup: false,
    // active conversation data
    activeConversation: null,
    activeConversationMessages: { options: new Map(), meta: {} },
    workflowRunningStatus: [WorkflowStatusEnum.RESUMED, WorkflowStatusEnum.PAUSED, WorkflowStatusEnum.START],
    // UI flags for loading states
    uiFlags: {
      isFetching: {
        conversations: false,
        media: false,
        galleryMedia: false,
        messages: false,
        counter: false,
        aiTransformationText: false,
        prevConversations: false,
      },
    },
    initialUiFlags: {
      initialLoad: {
        messages: false,
      },
    },
    errorFlags: {
      isError: {
        media: false,
        galleryMedia: false,
        messages: false,
        conversations: false,
        conversation: false,
      },
    },
    // Conversations list state
    conversationsData: {
      options: new Map(),
      meta: {},
      assignmentCounts: { all: 0, unassigned: 0, mine: 0 },
      nextCursor: null,
    },
    pendingConversations: new Map<string, ConversationType>(),
    // filter data
    filtrationParams: {
      assignType: CustomFilterAssignTypeConst.AssignedToMe,
      conversationsSorting: ConversationsSortingConst.LastActivityAt,
      conversationsStatus: ConversationsStatusConst.OPENED,
      sideBar: { type: 'all-conversations', id: 'all-conversations' },
      customFilter: null,
    },
    requestFilterData: null,
    conversationsChannels: [],
    // message editor data
    replayMessage: null,
    typingConversations: {},
    replayType: 1,
    // Request controllers for aborting pending requests
    requestControllers: {
      conversationDetails: null,
      messages: null,
      media: null,
      galleryMedia: null,
      allConversations: null,
    },
    // Messages controller data
    messagesPointer: {
      previous: 1,
      latest: 1,
    },
    cachedConversationsMessages: {},
    // ui status
    uiState: { slider: false, search: false, sidebarFilters: false },
    asidePanelState: initialAsidePanelState,
    openedFilters: JSON.parse(localStorage.getItem('openedFilters') || '[0,1,2]'),
    filtersPanelState: JSON.parse(localStorage.getItem('filtersPanelState') || 'false'),
  }),
  getters: {
    // ===================================== ACTIVE CONVERSATION GETTERS =====================================
    /**
     * Get the currently active conversation
     */
    getAssignedTeam(state): ConversationsTeamType | null {
      return (state.activeConversation as ConversationType).assigneeTeam;
    },
    /**
     * Get the assigned user for the active conversation
     */
    getAssignedUser(state): ConversationsUserType | null {
      return (state.activeConversation as ConversationType).assigneeUser;
    },
    /**
     * Get the priority level of the active conversation
     */
    getPriority(state): number | null {
      return (state.activeConversation as ConversationType).priority;
    },
    /**
     * return true if workflow is running
     */
    isWorkFlowRunning: (state): boolean => {
      return state.workflowRunningStatus.includes(state.activeConversation?.workflow?.status as string);
    },
    /**
     * return true if ai is running
     */
    isAiRunning: (state): boolean => {
      return state.activeConversation?.aiStatus === 'running';
    },
    // ===================================== UI STATE GETTERS =====================================
    /**
     * Returns true if any panel in asidePanelState is open (true).
     * @returns {boolean} True if at least one panel is active, false otherwise.
     */
    isAnyAsidePanelOpen: (state): boolean => {
      return Object.values(state.asidePanelState).some((status) => status);
    },
    isMessageButtonsDisabled: (state) => {
      const { contact, lastConversationMessageDate, isSoftDeleted, channel, workflow, isWebsiteClose } =
        state.activeConversation as ConversationType;

      const daysSinceLastMessage = moment().diff(moment(lastConversationMessageDate), 'days', true);
      const maxDays = conditionsDateType[channel?.type] ?? 0;

      return (
        ((contact?.isBlocked ||
          (maxDays && daysSinceLastMessage >= maxDays) ||
          (workflow && state.workflowRunningStatus.includes(workflow?.status))) &&
          state.replayType === ReplyTypeConst.Normal) ||
        channel.isDeleted ||
        isSoftDeleted ||
        isWebsiteClose
      );
    },
    // returns users typing in active conversations
    getActiveConversationsUsersTyping(state): ConversationUsersTyping {
      return state.typingConversations[state.activeConversation?.conversationId as string];
    },
  },
  actions: {
    // ===================================== UI STATE METHODS =====================================
    /**
     * Update uiState
     */
    updateUiState({ key, status }: { key: UiStateEnum; status: boolean }): void {
      this.uiState[key] = status;
    },
    /**
     * Update uiState
     */
    updateAsidePanelState({ key, status }: { key: AsidePanelStateEnum; status: boolean }): void {
      this.asidePanelState[key] = status;

      // Save the entire asidePanelState in localStorage
      if (this.asidePanelState) {
        localStorage.setItem('asidePanelState', JSON.stringify(this.asidePanelState));
      }
    },
    /**
     * Update uiState
     */
    updateOpenedFilters(filters: number[]): void {
      this.openedFilters = filters;

      localStorage.setItem('openedFilters', JSON.stringify(this.openedFilters));
    },
    updateFiltersPanelState(status: boolean): void {
      this.filtersPanelState = status;
      localStorage.setItem('filtersPanelState', JSON.stringify(status));
    },
    // ===================================== REQUEST CANCELLATION METHODS =====================================
    /**
     * Abort any previous requests of a specific type to prevent old requests from completing after switching conversations
     */
    abortRequestType(type: keyof typeof this.requestControllers): void {
      if (this.requestControllers[type]) {
        this.requestControllers[type]!.abort();
        this.requestControllers[type] = null;
      }
    },
    /**
     * Update or create a new AbortController for a specific request type
     */
    updateAbortController(type: keyof typeof this.requestControllers): AbortController {
      this.abortRequestType(type);
      const controller = new AbortController();
      this.requestControllers[type] = controller;
      return controller;
    },
    /**
     * Abort all pending requests for the current active conversation (called when switching conversations)
     */
    abortAllConversationRequests(): void {
      this.abortRequestType('conversationDetails');
      this.abortRequestType('messages');
      this.abortRequestType('media');
      this.abortRequestType('galleryMedia');
    },
    // ===================================== FILTRATION CONVERSATION METHODS =====================================
    /**
     * Set the channels list for conversations filtration, this method is called when fetching the channels list for the first time to save it in the store and use it later in the filtration process, it ensures that the channels data is easily accessible and up to date for any component that needs to use it for filtering conversations based on channels
     */
    setConversationsChannels(data: string[]): void {
      this.conversationsChannels = data;
    },
    /**
     * Set the filters for conversations list based on the provided parent and data, this method is called when the user applies filters from the sidebar or any other filter component, it updates the filtrationParams in the store which can then be used to fetch the filtered conversations list from the server, it also ensures that the filter data is structured in a way that can be easily consumed by the API when making requests for filtered conversations
     */
    setConversationsPipelineFilter(data: Partial<ConversationsFiltrationParams>): void {
      this.filtrationParams = { ...this.filtrationParams, ...data };

      if (Object.values(this.filtrationParams)) {
        conversationsSocket.sendAction('CONVERSATION_CURRENT_FILTER', this.filtrationParams);
        this.getAllConversations();
      }
    },
    // reset conversations filter
    resetConversationsPipelineFilter(): void {
      this.filtrationParams = {};
    },
    // reset conversations filter
    setDefaultConversationsFilter(): void {
      const router = getRouter();

      this.filtrationParams = {
        assignType: CustomFilterAssignTypeConst.AssignedToMe,
        conversationsSorting: ConversationsSortingConst.LastActivityAt,
        conversationsStatus: ConversationsStatusConst.OPENED,
        sideBar: { type: 'all-conversations', id: 'all-conversations' },
        customFilter: null,
      };

      router?.push({
        name: 'c-all-conversations',
      });

      this.getAllConversations();
    },
    /**
     * Normalize filters for API consumption
     */
    normalizeFiltersForApi(segmentFilters: any) {
      return segmentFilters.map((group: any) => ({
        ...group,
        filters: group.filters.map((filter: any) => ({
          ...filter,
          value: Array.isArray(filter.value)
            ? filter.value.map((v: any) => (typeof v === 'object' ? v?._id || v?.id || v?.value : v))
            : typeof filter.value === 'object'
              ? filter.value?._id || filter.value?.id
              : filter.value,
        })),
      }));
    },
    // ===================================== CONVERSATIONS METHODS =====================================
    /**
     * Reset all data related to the active conversation (called when switching conversations or when the active conversation is deleted)
     */
    resetActiveConversationData(): void {
      this.abortAllConversationRequests();

      // emit the current conversation for backend socket events filtration
      if (this.activeConversation) {
        socket.sendAction('CONVERSATIONS.LEAVE_CONVERSATION', {
          conversationId: (this.activeConversation as ConversationType).conversationId,
        });
      }

      this.activeConversation = null;
      this.activeConversationMessages = { options: new Map(), meta: {} };
      this.replayMessage = null;
      this.replayType = 1;
      this.uiState.slider = false;
      this.messagesPointer = {
        previous: 1,
        latest: 1,
      };

      // reset ui flags
      this.uiFlags.isFetching.messages = false;
      this.uiFlags.isFetching.media = false;
      this.uiFlags.isFetching.galleryMedia = false;
      this.initialUiFlags.initialLoad.messages = false;
      this.errorFlags.isError.conversation = false;
      this.errorFlags.isError.messages = false;
      this.errorFlags.isError.media = false;
      this.errorFlags.isError.galleryMedia = false;
    },
    /**
     * Reset all conversations related data in the store
     */
    resetAllConversationsData(): void {
      this.conversationsData = {
        options: new Map(),
        nextCursor: null,
        assignmentCounts: { all: 0, mine: 0, unassigned: 0 },
        meta: {},
      };
      this.requestFilterData = null;
      this.cachedConversationsMessages = {};
    },
    /**
     * Builds query parameters string for the conversations API request.
     * Includes filters, sorting, route params, meta type, and cursor for pagination.
     *
     * @param {boolean} isLoadMore - Whether this request is for loading more data.
     * @returns {string} The query string to use in the API call.
     */
    buildQueryParams(isLoadMore: boolean): string {
      // Short cut for custom filter cursor if applicable
      if (this.filtrationParams.customFilter) {
        return `cursor=${isLoadMore ? this.conversationsData?.nextCursor : ''}`;
      }

      const route = getRouter();
      const currentRoute = unref(route?.currentRoute);
      // Base query with filters and sorting
      let params =
        `filterationAssignType=${this.filtrationParams.customFilter ? null : this.filtrationParams.assignType || CustomFilterAssignTypeConst.AssignedToMe}` +
        `&${this.filtrationParams.conversationsSorting || ConversationsSortingConst.LastActivityAt}=true` +
        `&status=${this.filtrationParams.conversationsStatus || ConversationsStatusConst.OPENED}`;

      // Include allowed route params
      const filteredParams = this.getFilteredRouteParams();
      filteredParams.forEach(([key, value]) => {
        params += `&${key}=${value}`;
      });

      // Include meta type if exists
      if (currentRoute?.meta?.type) {
        params += `&${currentRoute.meta?.type}=true`;
      }

      // Add cursor for load more requests
      if (this.conversationsData?.nextCursor) {
        params += `&cursor=${isLoadMore ? this.conversationsData.nextCursor : ''}`;
      }

      return params;
    },
    /**
     * Returns route params excluding any that should not be included in the API query.
     *
     * @returns {[string, any][]} Array of key-value pairs for valid route params.
     */
    getFilteredRouteParams(): [string, any][] {
      const route = getRouter();
      const currentRoute = unref(route!.currentRoute);
      const EXCLUDED_ROUTE_PARAMS = ['conversationId', 'customFilterId', 'messageId', 'contactId'];
      return Object.entries(currentRoute.params).filter(([key]) => !EXCLUDED_ROUTE_PARAMS.includes(key));
    },
    /**
     * Fetch the conversations list based on the provided filters and query parameters, this method is called when the user applies filters(id) or when the conversations list needs to be refreshed, it also handles the loading state for both the conversations list and the counter in the sidebar, and it optimizes the API call by only fetching the counter data when it's not a load more action (since load more doesn't affect the counts)
     */
    getAllConversations({ isLoadMore = false, limit = 25 }: ConversationsRequestParamsType = {}): void {
      if (isLoadMore && this.uiFlags.isFetching.conversations) {
        return;
      }

      const route = getRouter();
      const currentRoute = unref(route!.currentRoute);
      this.errorFlags.isError.conversations = false;
      this.uiFlags.isFetching.conversations = true;
      const controller = this.updateAbortController('allConversations');

      const query = this.buildQueryParams(isLoadMore);
      const id = this.filtrationParams.customFilter ? (currentRoute?.params?.customFilterId as string) : '';
      const filterData = this.filtrationParams.customFilter;

      if (!isLoadMore) {
        this.resetAllConversationsData();
        this.requestFilterData = { query, id, filterData };
        this.uiFlags.isFetching.counter = true;
      }
      // to check if there is any same request work
      const isStillCurrent = () => this.requestControllers.allConversations === controller;

      conversationsApi
        .create(
          {
            main_relation: filterData?.relation,
            groups: this.normalizeFiltersForApi(filterData?.filters || []),
            isFilterationWithId: !!id,
            filterId: id,
          },
          `conversation/fetch-all?${query || ''}&limit=${limit}`,
          { signal: controller.signal, skipErrorToast: true },
        )
        .then((response: any) => {
          if (!isStillCurrent()) {
            return;
          }

          const responseData = response.data?.data;
          const conversations = responseData?.data || [];

          this.conversationsData.options = isLoadMore
            ? new Map([
                ...this.conversationsData.options,
                ...conversations.map((conversation: ConversationType) => [conversation.conversationId, conversation]),
              ])
            : new Map(
                conversations.map((conversation: ConversationType) => [conversation.conversationId, conversation]),
              );

          this.conversationsData.nextCursor = responseData.nextCursor;
          this.conversationsData.meta = responseData.meta || {};

          if (!isLoadMore) {
            this.conversationsData.assignmentCounts = responseData.assignmentCounts;
          }
        })
        .catch((error) => {
          if (!isStillCurrent()) {
            return;
          }
          if (!['AbortError', 'CanceledError'].includes(error.name)) {
            this.errorFlags.isError.conversations = true;
          }
        })
        .finally(() => {
          if (!isStillCurrent()) {
            return;
          }
          this.uiFlags.isFetching.conversations = false;
          if (!isLoadMore) {
            this.uiFlags.isFetching.counter = false;
          }
        });
    },
    /**
     * Fetch the details of a conversation by ID and set it as the active conversation, this method is called when the user navigates to a conversation route with an ID parameter, it ensures that the active conversation data is always up to date with the latest data from the server, and if the conversation is not found (e.g., invalid ID or deleted conversation) it redirects the user back to the all conversations view
     */
    async activeConversationDetails({ id, messageId }: { id: string; messageId?: string }): Promise<void> {
      this.uiFlags.isFetching.conversation = true;
      this.errorFlags.isError.conversation = false;
      const controller = this.updateAbortController('conversationDetails');
      await conversationsApi
        .show(`conversation/${id}`, { signal: controller.signal, skipErrorToast: true })
        .then(async (response: any) => {
          this.activeConversation = response.data.data;
          await this.fetchConversationMessages({ isInitialLoad: true, messageId });
        })
        .catch((error: any) => {
          // Don't redirect if request was aborted (user switched conversations)
          if (!['AbortError', 'CanceledError'].includes(error.name)) {
            if (error.response.data.statusCode === 404) {
              const router = getRouter();
              this.resetActiveConversationData();
              router?.push({ name: 'c-all-conversations' });
            } else {
              this.errorFlags.isError.conversation = true;
            }
          }
        })
        .finally(() => {
          this.uiFlags.isFetching.conversation = false;
        });
    },
    /**
     * Set the active conversation when user clicks on a conversation from the list, this method is used in multiple places to ensure that the active conversation data is always set correctly when the user navigates to a conversation, it also resets the active conversation messages and attachments data to avoid showing old data while the new data is being fetched, and it calls the getConversationMessage method to fetch the messages for the newly active conversation
     */
    setActiveConversation(data: ConversationType): void {
      this.resetActiveConversationData();
      this.activeConversation = data;

      // emit the current conversation for backend socket events filtration
      socket.sendAction('CONVERSATIONS.JOIN_CONVERSATION', {
        conversationId: (this.activeConversation as ConversationType).conversationId,
      });

      if (this.cachedConversationsMessages[data.conversationId]) {
        const cachedData = this.cachedConversationsMessages[data.conversationId];
        this.activeConversationMessages!.options = new Map(cachedData.options);
        this.activeConversationMessages!.meta = { ...cachedData.meta };
        this.messagesPointer = { ...cachedData.pointer };
      } else {
        this.fetchConversationMessages({ isInitialLoad: true });
      }
    },
    /**
     * Set the message data to be replayed in the message editor
     */
    setReplayMessage(data: MessageType): void {
      this.replayMessage = data;
    },
    /**
     * Reset the replay message data
     */
    resetReplayMessage(): void {
      this.replayMessage = null;
    },
    /**
     * Update the active conversation data when socket events come with updated conversation data, this method is used in multiple socket event handlers to ensure that the active conversation data is always up to date with the latest changes from the server, it also ensures that the conversation is only updated if it is the currently active one to avoid unnecessary updates when the user is viewing a different conversation
     */
    updateActiveConversation(data: ConversationType) {
      this.activeConversation = data;
    },
    /**
     * Add a user to the typing users list when they start typing
     */
    setTypingUser(data: TypingType) {
      if (!this.typingConversations[data.conversationId]) {
        this.typingConversations[data.conversationId] = {};
      }
      this.typingConversations[data.conversationId][data.userData.id] = data.userData;
    },
    /**
     * Remove a user from the typing users list when they stop typing
     */
    removeTypingUser(data: TypingType) {
      const targetedConversation = this.typingConversations[data.conversationId];
      if (targetedConversation) {
        delete targetedConversation[data.userData.id];

        if (!Object.keys(targetedConversation).length) {
          delete this.typingConversations[data.conversationId];
        }
      }
    },
    /**
     * Handle the counter update for the conversations list, this method is called when socket event fire after updating or inserting a conversation to ensure that the assignment counts are updated accordingly.
     */
    handelCounterUpdate(data: AssignmentValueType): void {
      this.uiFlags.isFetching.counter = true;
      this.conversationsData.assignmentCounts = { ...this.conversationsData.assignmentCounts, ...data };
      this.uiFlags.isFetching.counter = false;
    },
    initConversationWorker() {
      conversationWorker.onMessage(this.handleWorkerResponse);
    },
    handleWorkerResponse(payload: { conversationId: string; pathFilter: boolean }) {
      const data = this.pendingConversations.get(payload.conversationId);

      if (!data) {
        return;
      }

      if (payload.pathFilter) {
        this.conversationsData.options.set(data.conversationId, data);
      } else {
        this.conversationsData.options.delete(data.conversationId);
      }

      this.pendingConversations.delete(payload.conversationId);
    },
    /**
     * Unified method to update or insert a conversation in the conversations list based on the incoming data and the caller context, it uses the filterConversation helper to determine if the conversation should be included in the current list based on the active filters, and it optimizes the counter update by only calling it when necessary (not on every message update)
     */
    updateOrInsertConversation(data: ConversationType) {
      if (!data) {
        return;
      }

      this.pendingConversations.set(data.conversationId, data);
      const userData = useUserStore().userDetails;

      // Unwrap proxies; postMessage structured-clones the plain payload
      conversationWorker.post({
        conversation: toPlain(data),
        filters: toPlain(this.filtrationParams),
        allowedChannels: toPlain(this.conversationsChannels),
        userId: userData?.id,
      });
    },
    /**
     * Delete a conversation from the conversations list when a contact is deleted, and if the deleted conversation is the active one, reset the active conversation data and redirect to all conversations view
     */
    deleteItemFromConversationsList(data: DeleteContactType) {
      if (this.conversationsData.options.has(data.conversationId)) {
        this.conversationsData.options.delete(data.conversationId);
      }
    },
    /**
     * Delete the active conversation if the deleted conversation is the active one, and redirect to all conversations view
     */
    deleteActiveConversation(data: DeleteContactType) {
      if ((this.activeConversation as ConversationType).conversationId === data.conversationId) {
        const router = getRouter();

        this.resetActiveConversationData();
        router?.push({ name: 'c-all-conversations' });
      }
    },
    stopWorkflow(): void {
      const { success } = useToast();
      const oldWorkflow = (this.activeConversation as ConversationType).workflow;

      (this.activeConversation as ConversationType).workflow = undefined;
      this.updateOrInsertConversation({
        ...this.activeConversation!,
        workflow: undefined,
      });
      WorkflowApi.create(
        { conversationId: (this.activeConversation as ConversationType).conversationId },
        'workflows/stop',
      )
        .then(() => {
          success(trans('conversations.workflow.stop-successfully_trans'));
        })
        .catch(() => {
          (this.activeConversation as ConversationType).workflow = { ...oldWorkflow! };
          this.updateOrInsertConversation({
            ...this.activeConversation!,
            workflow: oldWorkflow,
          });
        });
    },
    // ===================================== MESSAGES METHODS =====================================
    /**
     * Unified method to fetch conversation data (messages, media attachments, gallery attachments) based on the provided fetch type and options, this method optimizes the fetching logic by using a single method for all three types of data, it also handles the loading states for each type separately to ensure that the UI can show appropriate loading indicators based on what data is being fetched, and it builds the request query dynamically based on the options provided to support pagination and filtering for messages and attachments
     */
    async fetchConversationMessages(options: ConversationsFetchOptions) {
      // Build query
      const {
        page = 1,
        isLoadingMore = false,
        messageId = null,
        toLatestMessages = false,
        limit = 25,
        isInitialLoad = false,
      } = options;

      const requestQuery: RequestQuery = { limit };
      if (messageId) {
        requestQuery.messageId = messageId;
      } else {
        requestQuery.page = page;
      }

      // Capture the current conversation ID at request time to verify response belongs to current conversation
      const requestConversationId = (this.activeConversation as ConversationType).conversationId;
      // Create new abort controller for this request
      const controller = this.updateAbortController(RequestUrlDestinationConst.MESSAGES);

      // reset error flag and set loading flag
      this.errorFlags.isError.messages = false;
      if (isInitialLoad) {
        this.initialUiFlags.initialLoad.messages = true;
      } else {
        this.uiFlags.isFetching.messages = true;
      }
      try {
        const response = await conversationsApi.create(
          { ...requestQuery, conversationId: requestConversationId },
          'messages/offset-pagination/v1',
          {
            signal: controller.signal,
            skipErrorToast: true,
          },
        );
        // Only update state if we're still in the same conversation (prevent stale updates from race conditions)
        if ((this.activeConversation as ConversationType).conversationId === requestConversationId) {
          const newMessages = response.data.data;
          this.activeConversationMessages.options = isLoadingMore
            ? new Map(
                toLatestMessages
                  ? [
                      ...newMessages.map((item: MessageType): [string, MessageType] => [item._id, item]),
                      ...this.activeConversationMessages.options,
                    ]
                  : [
                      ...this.activeConversationMessages.options,
                      ...newMessages.map((item: MessageType): [string, MessageType] => [item._id, item]),
                    ],
              )
            : new Map(newMessages.map((item: MessageType): [string, MessageType] => [item._id, item]));

          this.activeConversationMessages.meta = response.data.meta;

          if (isInitialLoad) {
            this.messagesPointer = {
              previous: this.activeConversationMessages.meta.current_page || 1,
              latest: this.activeConversationMessages.meta.current_page || 1,
            };
          }

          if (this.messagesPointer.latest === 1 && this.messagesPointer.previous >= 1) {
            this.cacheConversationMessage(requestConversationId, {
              options: this.activeConversationMessages.options,
              meta: this.activeConversationMessages.meta,
              pointer: this.messagesPointer,
            });
          }
        }
      } catch (error: any) {
        // Only log errors that are not from request cancellation
        if (!['AbortError', 'CanceledError'].includes(error?.name)) {
          this.errorFlags.isError.messages = true;
        }
      } finally {
        // Only clear loading flag if we're still in the same conversation
        if ((this.activeConversation as ConversationType)?.conversationId === requestConversationId) {
          if (isInitialLoad) {
            this.initialUiFlags.initialLoad.messages = false;
          } else {
            this.uiFlags.isFetching.messages = false;
          }
        }
      }
    },
    async checkLatestMessages() {
      const cachedConversation = this.cachedConversationsMessages[this.activeConversation!.conversationId];

      if (cachedConversation) {
        this.activeConversationMessages.options = cachedConversation.options;
        this.activeConversationMessages.meta = cachedConversation.meta;
        this.messagesPointer = cachedConversation.pointer;
      } else {
        await this.fetchConversationMessages({
          isInitialLoad: true,
        });
      }
    },
    /**
     * Increments the message pointer for a given direction key.
     */
    changeMessagePointer(key: 'previous' | 'latest'): void {
      if (key === 'previous') {
        ++this.messagesPointer[key];
      } else {
        --this.messagesPointer[key];
      }
    },
    /**
     * Set the replay type for the message editor (1 for normal reply, 2 for private note)
     */
    setMessageReplyType(type: (typeof ReplyTypeConst)[keyof typeof ReplyTypeConst]): void {
      this.replayType = type;
      /**
       * insure to reset if the user come form privet replay to normal replay
       * to avoid any issue with the message format
       */
      if (type === ReplyTypeConst.Normal) {
        this.resetReplayMessage();
      }
    },
    /**
     * Reset the unread messages count for the active conversation (called when user views the conversation)
     * from client side to update the UI immediately without sending a request to the server(need to implement from server side also)
     */
    markConversationAsRead(): void {
      socket.sendAction('CONVERSATIONS.CONVERSATION_READ', {
        conversationId: (this.activeConversation as ConversationType).conversationId,
      });
    },
    /**
     * Resend a failed message for agent and create a new message with the same content and attachments but with a new virtual ID, and the old one will be kept in the messages list with the failed status to show the error message in the UI.
     */
    resendAgentMessage(id: string, virtualId?: string): void {
      conversationsApi.create({ messageId: id, virtualId }, 'messages/retry-failed');
    },
    /**
     * Retry a failed message for contact (customer), this method will update the message status to loading and then call the API to resend the message, if the API call fails it will update the message status to failed and set the error message to be shown in the UI
     */
    retryContactMessage(id: string): void {
      // find the message in the active conversation messages
      const message = (this.activeConversationMessages as MessagesDataType).options.get(id);
      if (!message) {
        return;
      }

      message.isLoading = true;
      conversationsApi
        .create({ messageId: id }, 'messages/retry-media')
        .catch((error) => {
          message.content.isFailed = true;
          message.content.errorMessage = error.response?.data?.message || 'Unknown error';
        })
        .finally(() => {
          message.isLoading = false;
        });
    },
    updateOrInsertMessage(data: MessageType): void {
      const options = this.activeConversationMessages?.options;
      const exists = (data.virtualId && options?.has(data.virtualId)) || options?.has(data._id);

      if (exists) {
        this.updateMessage(data);
        return;
      }

      this.addNewMessage(data);
    },
    /**
     * Add a new message to the top of the active conversation messages list (used for new incoming messages or optimistic UI updates for sent messages)
     */
    addNewMessage(data: MessageType): void {
      if (!data) {
        return;
      }

      const messageSelector = data.virtualId || data._id;
      (this.activeConversationMessages as MessagesDataType).options = new Map([
        [messageSelector, data],
        ...(this.activeConversationMessages as MessagesDataType).options,
      ]);
    },
    /**
     * Unified method to update a message in the active conversation messages list.
     * Supports three scenarios:
     * 1. Update by virtualId (for pending messages) → if not found, adds as new message
     * 2. Update by _id only (for existing messages) → if not found, silently ignored
     * @param data The message data to update
     * @param allowAddNew If true, adds message as new if not found (default: virtualId exists)
     */
    updateMessage(data: MessageType): void {
      // check first may message exists if current agent add it
      if (data?.virtualId && this.activeConversationMessages?.options.has(data.virtualId)) {
        this.activeConversationMessages?.options.set(data.virtualId, data);
        return;
      }

      if (this.activeConversationMessages?.options.has(data._id)) {
        this.activeConversationMessages?.options.set(data._id, data);
        return;
      }
    },
    /**
     * Update the status of a message (e.g., sent, delivered, read) in the active conversation messages list
     */
    updateMessageStatus(data: MessagesStatusUpdate): void {
      const message = (this.activeConversationMessages as MessagesDataType).options.get(data.messageId);
      if (message) {
        message.status = data.status;
      }
    },
    async fetchAttachmentsData({
      params,
      selectedTypes = ['all'],
      messageId,
      limit = 25,
    }: {
      params?: any;
      selectedTypes?: string[];
      messageId?: string;
      limit?: number;
    }): Promise<{ data: MessageType[]; meta: DefaultMetaType }> {
      const response = await conversationsApi.create(
        {
          ...params,
          mediaTypes: selectedTypes,
          conversationId: this.activeConversation?.conversationId,
          messageId,
          limit,
        },
        'messages/media/offset-pagination',
      );

      return response.data;
    },
    // ===================================== CAcHe CONVERSATIONS MESSAGES =====================================
    /**
     * Unified method to update a cache message in the cached conversation messages list.
     * Supports three scenarios:
     * 1. Update by virtualId (for pending messages) → if not found, adds as new message
     * 2. Update by _id only (for existing messages) → if not found, silently ignored
     * @param data The message data to update
     * @param allowAddNew If true, adds message as new if not found (default: virtualId exists)
     */
    updateCacheMessage(message: MessageType): void {
      if (this.cachedConversationsMessages[message.conversationId!]) {
        return;
      }

      if (this.cachedConversationsMessages[message.conversationId!].options.has(message._id)) {
        this.cachedConversationsMessages[message.conversationId!].options.set(message._id, message);
      }
    },
    /**
     * Add a new cache message to the top of the cache conversation messages list (used for new incoming messages or optimistic UI updates for sent messages)
     */
    addNewCacheMessage(message: MessageType): void {
      if (this.cachedConversationsMessages[message.conversationId!]) {
        this.cachedConversationsMessages[message.conversationId!].options = new Map([
          [message._id, message],
          ...this.cachedConversationsMessages[message.conversationId!].options,
        ]);
      }
    },
    /**
     * Update the status of a cached message (e.g., sent, delivered, read) in the conversation cached messages list
     */
    updateCachedMessageStatus(message: MessagesStatusUpdate): void {
      if (this.cachedConversationsMessages[message.conversationId]) {
        const currentMessage = this.cachedConversationsMessages[message.conversationId].options.get(message.messageId);
        if (currentMessage) {
          currentMessage.status = message.status;
        }
      }
    },
    /**
     * Cache opened conversations messages and meta limit for it is 10 conversations
     */
    cacheConversationMessage(conversationId: string, value: CachedMessagesType): void {
      this.cachedConversationsMessages[conversationId] = {
        options: new Map(value.options),
        meta: { ...value.meta },
        pointer: { ...value.pointer },
      };
      const keys = Object.keys(this.cachedConversationsMessages);
      if (keys.length > 10) {
        delete this.cachedConversationsMessages[keys[0]]; // remove oldest
      }
    },
    // ===================================== CONVERSATION CONTACT METHODS =====================================
    /**
     * Add a tag to the active conversation's contact
     */
    addContactTag(tag: tagType): void {
      if (!this.activeConversation?.tags) {
        return;
      }
      this.activeConversation.tags.push(tag);
    },
    /**
     * Remove a tag from the active conversation's contact
     */
    removeContactTag(tag: tagType): void {
      if (!this.activeConversation?.tags) {
        return;
      }
      this.activeConversation.tags = this.activeConversation.tags.filter(
        (t) => t.contactServiceId !== tag.contactServiceId,
      );
    },
    // ===================================== SOCKET CONNECTION METHODS =====================================
    /**
     * Toggle the socket connection status, this method can be used to manually trigger a reconnection attempt when the connection is lost, and it can also be used for testing purposes to simulate connection loss and recovery scenarios in the UI
     */
    toggleSocketConnection(): void {
      this.socketConnection = !this.socketConnection;
    },
    /**
     * Save the socket connection error message to the store, this method is called when the socket connection encounters an error and reaches the maximum number of reconnection attempts, it allows the UI to display a meaningful error message to the user in the reconnection popup, and it can also be used for debugging purposes to log or analyze connection issues
     */
    saveSocketError(error: string): void {
      this.connectionError = error;
    },
    /**
     * Toggle the visibility of the reconnection popup, this method is called when the socket connection encounters an error and reaches the maximum number of reconnection attempts to show the popup to the user, and it can also be used to hide the popup when the user acknowledges the error or when a new connection attempt is made
     */
    toggleSocketReconnectionPopup(): void {
      this.reconnectionPopup = !this.reconnectionPopup;
    },
    // ===================================== HELPERS METHODS =====================================
    /**
     * Reset the entire conversations store to its initial state, this method can be used when the user logs out to clear all conversations data from the store and ensure a clean state for the next user session
     */
    resetStore(): void {
      this.$reset();
    },
  },
});
